import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type TextareaHTMLAttributes
} from 'react';
import { getMentionComposeHint, hasIncompleteMention } from './mentionEmail';
import { MentionCandidatePicker } from './MentionCandidatePicker';
import { MentionComposePanel } from './MentionComposePanel';
import { DiscussionComposeToolbar } from './emoji/DiscussionComposeToolbar';
import { insertTextAtComposeCaret } from './emoji/insertAtComposeCaret';
import { applyMentionToken, getMentionPickerQuery } from './mentionPicker';
import { configureComposeAssist } from './configureComposeAssist';
import './mentionEmail.css';

export interface AnchorCommentTextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'ref'> {
  mountId?: string;
  /** File path within the mount; scopes WebOps parent mounts to the covering client folder. */
  path?: string;
}

export function validateAnchorCommentText(text: string): string | null {
  if (hasIncompleteMention(text)) {
    return 'Finish each mention from the picker or remove @ before saving.';
  }
  return null;
}

export function AnchorCommentTextarea({
  onChange,
  value,
  disabled,
  className,
  mountId,
  path,
  ...rest
}: AnchorCommentTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const bindTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    configureComposeAssist(el);
  }, []);
  const composeText = typeof value === 'string' ? value : '';
  const [pickerOpen, setPickerOpen] = useState(false);
  const hintMessage = useMemo(
    () => (!disabled ? getMentionComposeHint(composeText) : null),
    [composeText, disabled]
  );
  const pickerQuery = useMemo(
    () => (!disabled && mountId ? getMentionPickerQuery(composeText) : null),
    [composeText, disabled, mountId]
  );

  const onPick = useCallback(
    (token: string) => {
      if (!pickerQuery || !onChange) return;
      const next = applyMentionToken(composeText, pickerQuery.triggerIndex, token);
      onChange({
        target: { value: next }
      } as ChangeEvent<HTMLTextAreaElement>);
      setPickerOpen(false);
    },
    [composeText, onChange, pickerQuery]
  );

  const onEmojiPick = useCallback(
    (emoji: string) => {
      const el = textareaRef.current;
      if (!el || !onChange) return;
      const next = insertTextAtComposeCaret(el, emoji);
      onChange({
        target: { value: next }
      } as ChangeEvent<HTMLTextAreaElement>);
    },
    [onChange]
  );

  const showPicker = Boolean(mountId && pickerQuery && (pickerOpen || pickerQuery));

  return (
    <div className="file-review-mention-input-wrap">
      <DiscussionComposeToolbar onEmojiSelect={onEmojiPick} disabled={disabled} />
      <textarea
        {...rest}
        ref={bindTextareaRef}
        className={className}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onChange?.(e);
          if (mountId && getMentionPickerQuery(e.target.value)) setPickerOpen(true);
        }}
        onFocus={() => {
          if (mountId && pickerQuery) setPickerOpen(true);
        }}
      />
      <MentionComposePanel
        hintMessage={hintMessage}
        showPicker={showPicker}
        picker={
          mountId && pickerQuery ? (
            <MentionCandidatePicker
              mountId={mountId}
              path={path}
              query={pickerQuery.query}
              open={showPicker}
              onOpenChange={setPickerOpen}
              onSelect={onPick}
            />
          ) : null
        }
      />
    </div>
  );
}
